import { Cloud, History, Layers, Upload } from 'lucide-react';
import type { HelpCategory } from './types';

// Sunstone Help page content (SUN-412 / help redesign).
//
// Authored as a .ts module (not .md) on purpose: the UI container build excludes all markdown
// from its build context (the **/*.md entry in ui.garden.yaml), so a markdown asset import passes
// local `vite build` but fails the Garden/Docker build with ENOENT. This is a living list — add a
// question to the right category as questions come up. `slug` is the deep-link anchor for a
// question (`/help#slug`); pick it once and don't change it, even if the wording changes later.
export const helpCategories: HelpCategory[] = [
  {
    id: 'concepts',
    label: 'Concepts',
    icon: Layers,
    questions: [
      {
        slug: 'hierarchy',
        question: "What's the difference between a partner, customer, deployment, and workspace?",
        answer: `Sunstone is organized as a hierarchy, from the top down:

- **Partner** — a top-level organization (for example, a reseller, or Uniphore itself). A partner
  can contain one or more customers. It can also contain deployments directly.
- **Customer** — an organization that belongs to a partner. A customer can contain one or more
  deployments.
- **Deployment** — an isolated cloud environment (e.g. AWS, Azure, On-Prem) that runs a
  DuploCloud instance. A deployment can be created under a customer or directly under a partner.
  Deployments can contain one or more workspaces.
- **Workspace** — a single Duplo tenant where application services actually run.

How they nest:

- A **partner** contains **customers** or **deployments**
- A **customer** contains **deployments**
- A **deployment** contains **workspaces**`,
      },
      {
        slug: 'creating-entities',
        question: 'How do I create a new partner, customer, deployment, or workspace?',
        answer: `- **Partner** — **Partners → New Partner**. Enter a name, URL slug, and primary contact.
- **Customer** — **Customers → New Customer**. Choose the **partner** it belongs to, then enter
  a name and slug.
- **Deployment** — open the partner or customer, then **New Deployment**. Pick the provider
  (e.g. AWS), the deployment model (Hybrid / SaaS / Airgapped), and the region.
- **Workspace** — open an **Active** deployment, then **New Workspace**. Give it a slug, pick the
  environment (development / staging / production), release version, and Duplo infra to install
  into.`,
      },
    ],
  },
  {
    id: 'deployment-models',
    label: 'Deployment models',
    icon: Cloud,
    questions: [
      {
        slug: 'deployment-models',
        question: 'What deployment models are available, and how do they differ?',
        answer: `When you create a deployment you pick a **model**. It captures who runs the Management
Plane and whose infrastructure the workloads run in:

- **Hybrid** — Uniphore-managed Management Plane, **customer** infrastructure. Uniphore runs
  the management/control planes while the workloads run in the customer's own cloud account.
  This is the bring-your-own-cloud path: creating the deployment produces a CloudFormation
  template that is run in the customer's AWS account to grant the access Sunstone needs, after
  which it bootstraps and provisions.
- **SaaS** — Uniphore-managed Management Plane, **Uniphore** infrastructure. BAIC runs in
  Uniphore-owned cloud rather than the customer's own account (e.g. the BAIC-DEV environment).
- **Airgapped** — Customer-managed Management Plane, **customer** infrastructure. The customer
  operates everything independently, with no connectivity back to Uniphore (for air-gapped
  environments).`,
      },
    ],
  },
  {
    id: 'releases',
    label: 'Releases',
    icon: Upload,
    questions: [
      {
        slug: 'release-visibility',
        question: 'Where do new releases show up?',
        answer: `New releases show up on the **Releases** page — reachable from a workspace's
**Upgrade** tab or the dashboard's **Release events** card.`,
      },
      {
        slug: 'sync-plan-promote',
        question: 'How do I sync, plan, and promote a release?',
        answer: `Releases move through four stages, from the **Releases** page into a workspace's
**Upgrade** tab:

\`\`\`text
┌────────┐     ┌──────────────────┐     ┌──────────────────┐     ┌────────────────────┐
│  Sync  │ --> │  Preview / Diff  │ --> │ Plan (optional)  │ --> │  Promote / Apply   │
└────────┘     └──────────────────┘     └──────────────────┘     └────────────────────┘
 pulls the       compares the             a real dry-run,           makes the change
 release from    workspace's deployed     changes nothing           and restarts
 x-forge —       manifest against the                               services
 shared by       release you synced
 every workspace
 on that release
\`\`\`

1. **Sync** — refreshes the release's manifest from x-forge-installer. It's a release-level
   action, not per-workspace: sync a release once and every workspace targeting it sees the
   update.
2. **Preview / Diff** — open a workspace, go to the **Upgrade** tab, and choose **Preview Upgrade
   to {version}** (or **Release Sync & Promote** from the workspace page) to see exactly what
   would change.
3. **Plan** — an optional button that appears once the diff is ready. It's a real Terraform
   dry-run against your infrastructure, but it changes nothing — safe to run as many times as you
   like.
4. **Promote / Apply** — the real change. **Promote to {version}** moves the workspace to a new
   release; **Apply** re-runs the same release version already deployed. Both restart services,
   so you'll be asked to confirm before either one runs.

See the next few questions for what each stage shows you and what can block it.`,
      },
      {
        slug: 'release-sync-and-diff',
        question: 'What does Sync do, and what does the diff/preview actually show me?',
        answer: `**Sync** dispatches x-forge-installer's release-sync job for a specific release and pulls its
manifest into Sunstone. Because it targets the *release*, not a workspace, you only need to sync a
given release once — every workspace that targets it can then preview and promote to it. Sunstone
also requires a **reasonably fresh sync**: if a release was synced too long ago (or never synced),
you'll be asked to sync again before you can plan or promote.

The **Preview / Diff** compares what's currently deployed in the workspace against the release you
synced. It has three possible states:

| Diff status | Meaning | What to do |
|---|---|---|
| **Ready** | The diff computed successfully. | Review the diff table, then Plan or Promote. |
| **Sync needed** | The release has never been synced, or the sync is too old. | Go sync the release, then re-open the preview. |
| **Failed** | Sunstone couldn't compute the diff. | Check the workspace's **History** tab for the error, or try syncing again. |`,
      },
      {
        slug: 'plan-vs-promote-vs-apply',
        question:
          'What\'s the difference between Plan, Promote, and Apply — and what does "Run Liquibase migrations" do?',
        answer: `- **Plan** runs a real Terraform \`plan\` (dry-run) against your infrastructure and streams the
  output live in the dialog, with a **View in GitHub** link once the run registers. It doesn't
  change any infrastructure and doesn't update the workspace's status — run it as many times as
  you want before deciding to promote.
- **Promote to {version}** and **Apply** run the same underlying job with Terraform \`apply\` — the
  only difference is *what* they apply:
  - **Promote to {version}** moves the workspace to a **different** release version.
  - **Apply** re-runs the **same** release version already deployed (for example, to pick up an
    infrastructure fix without bumping the version).

  Both restart services, so you'll be asked to confirm ("This will restart services... I
  understand and want to continue") before either one runs.
- The **Run Liquibase migrations** checkbox, available on both Plan and Promote/Apply, controls
  whether database migrations bundled with the release are executed as part of the run. Leave it
  checked for a normal upgrade that includes schema changes; only uncheck it if you need to apply
  infrastructure changes without touching the database.`,
      },
      {
        slug: 'upgrade-blocked-reasons',
        question: "Why can't I Plan or Promote right now?",
        answer: `A few things can block Plan or Promote on a workspace:

- **An upgrade is already in progress** — Sunstone allows only one Plan or Promote at a time per
  workspace. If the workspace's upgrade status is already **In progress**, wait for it to finish
  before starting another.
- **The release is deprecated** — a release marked deprecated on the **Releases** page can no
  longer be promoted to. Pick a supported release instead.
- **The sync is stale or missing** — see the diff status table in the previous question; you'll be
  asked to sync again.
- **The workspace has no current release recorded** — some older, Duplo-discovered workspaces were
  never linked to a Sunstone release. If you don't see Sync/Plan/Promote options at all on a
  workspace, this is usually why — re-running **Import Tenants** in x-forge-installer links the
  workspace to its release so these actions become available.`,
      },
      {
        slug: 'promote-failed-next-steps',
        question: 'A Plan or Promote run failed — where do I look?',
        answer: `Open the workspace's **History** tab — the failed row expands automatically and shows an
**Error output** block with the failure detail. From there you can also follow the **GitHub run**
link to the underlying x-forge-installer Actions job for full logs.

A couple of things worth knowing:
- A **failed Plan** doesn't touch the workspace's status — it's a dry-run, so you can fix whatever
  the error points at and just try again.
- A **failed Promote/Apply** updates the workspace's upgrade status to **Failed**. Check the error
  output first; if it's not obvious, the linked GitHub run has the full job logs.`,
      },
    ],
  },
  {
    id: 'history',
    label: 'History & troubleshooting',
    icon: History,
    questions: [
      {
        slug: 'event-history',
        question: 'Where can I see the history of what happened to a deployment or workspace?',
        answer: `Both deployments and workspaces keep an audit trail on a **History** tab.
**Failed rows expand automatically** and include an **Error output** block with the failure
detail.`,
      },
      {
        slug: 'failure-logs',
        question: 'A row failed — how do I find out why?',
        answer: `Start with the **Error output** block on the failed row in the **History** tab. Where
available, rows also link out to the underlying run — **GitHub run** (the x-forge GitHub Actions
job) and **Temporal** (the workflow execution) — for the full logs when the Error output isn't
enough.`,
      },
    ],
  },
];
